// src/utils/tenant.js - Centralized tenant management utilities

/**
 * Extracts the tenant name from the current hostname
 * Falls back to localStorage if hostname doesn't contain a subdomain
 * @returns {string} The tenant name
 */
export const extractTenantFromHostname = () => {
    const hostname = window.location.hostname;
    
    // If hostname includes a dot, extract the first segment as tenant
    if (hostname.includes('.')) {
      const tenant = hostname.split('.')[0];
      // Update localStorage with current tenant
      localStorage.setItem('tenant', tenant);
      return tenant;
    }
    
    // For localhost development without subdomain, fallback to localStorage
    const storedTenant = localStorage.getItem('tenant');
    if (storedTenant) {
      return storedTenant;
    }
    
    // Ultimate fallback - should rarely be used
    console.warn('No tenant information found! Using default tenant.');
    return 'default';
  };
  
  /**
   * Creates a tenant-aware query key for React Query
   * Ensures that cached data is isolated by tenant
   * @param {Array|string} baseKey - The original query key
   * @returns {Array} Tenant-prefixed query key
   */
  export const createTenantQueryKey = (baseKey) => {
    const tenant = extractTenantFromHostname();
    if (Array.isArray(baseKey)) {
      return [tenant, ...baseKey];
    }
    return [tenant, baseKey];
  };
  
  /**
   * Detects if there was a tenant change by comparing current hostname
   * with the tenant stored in localStorage
   * @returns {boolean} True if tenant has changed
   */
  export const hasTenantChanged = () => {
    const currentTenant = extractTenantFromHostname();
    const storedTenant = localStorage.getItem('tenant');
    
    // If stored tenant exists and doesn't match current, tenant has changed
    return storedTenant && storedTenant !== currentTenant;
  };
  
  /**
   * Determines the appropriate API URL for the current tenant and environment
   * @param {string} [path=''] - Optional API path to append to the base URL
   * @returns {string} Complete API URL
   */
  export const getTenantApiUrl = (path = '') => {
    const tenant = extractTenantFromHostname();

    // Base API URL based on environment (read from runtime config)
    const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

    let baseUrl;
    if (nodeEnv === 'development') {
      baseUrl = `http://${tenant}.api.wygc.eu:8080/api`;
    } else {
      baseUrl = `https://${tenant}.api.wygc.eu/api`;
    }

    // Clean up the path and append it to the base URL
    const cleanPath = path.replace(/^\//, '');
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
  };
  
  /**
   * Resets tenant-specific data in localStorage while preserving the tenant identifier
   * Useful during logout to clear user data but keep tenant context
   */
  export const resetTenantData = () => {
    // Save the tenant before clearing localStorage
    const tenant = localStorage.getItem('tenant');
    
    // Clear all tenant-specific data
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userTeamId');
    localStorage.removeItem('username');
    localStorage.removeItem('teamName');
    
    // Keep tenant for subsequent logins
    if (tenant) {
      localStorage.setItem('tenant', tenant);
    }
  };
  
  /**
   * Stores data related to the current tenant
   * @param {Object} data - Data to store in localStorage
   */
  export const storeTenantData = (data) => {
    const tenant = extractTenantFromHostname();
    
    // Store tenant information
    localStorage.setItem('tenant', tenant);
    
    // Store other tenant-related data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, value);
      }
    });
  };